import './FundItem.sass';
import './ETFItem.sass';
import axios from 'axios';
import imgFundHeroL from '../assets/fundlist/fund-hero-l.jpg';
import imgEtfHeroL from '../assets/fundlist/etf-hero-l.jpg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetcher, useStockItem, useStockPrice, useStockList } from '../api';
import Breadcrumbs from '../components/Breadcrumbs';
import { useParams, useLocation, useHistory } from 'react-router';
import { find, sortBy, groupBy, each } from 'lodash';
import { Link } from 'react-router-dom';
import YearMonthSelector from '../components/YearMonthSelector';
import { usePrevious } from 'react-use';
import Loading from './Loading';
import getTimestamp from '../library/tools/getTimestamp';
import moment from 'moment';
import ChartComponent from './ChartComponent';
import none from '../assets/dividend/none.svg';
import Meta from '../components/Meta';
import BaseUrl from '../api/baseUrl';

const ETFItem = () => {
	const params = useParams();
	const location = useLocation();
	const [pathName, setPathName] = useState('');
	const history = useHistory();

	const dict = useMemo(() => {
		return {
			onshore: {
				pathName: '境內基金',
				categoryId: 1,
				imgL: imgFundHeroL,
			},
			offshore: {
				pathName: '境外基金',
				categoryId: 2,
				imgL: imgFundHeroL,
			},
			etf: {
				pathName: 'ETF',
				categoryId: 3,
				imgL: imgEtfHeroL,
			},
		};
	}, []);

	const [currentCategory, setCurrentCategory] = useState('');
	const [currentFund, setCurrentFund] = useState('');

	const category = useMemo(() => {
		const { pathname } = location;

		if (pathname.includes('onshore')) return 'onshore';
		if (pathname.includes('offshore')) return 'offshore';
		if (pathname.includes('etf')) return 'etf';
	}, [location]);

	useEffect(() => {
		setCurrentCategory(category);
	}, [category]);

	const { list } = useStockList();

	const listWithCurrentItemMarked = useMemo(() => {
		return list.map((item) => {
			const isCurrent = item.result.some((result) => result.fundID === params.fundID);
			if (isCurrent) setCurrentFund(item.id);
			return { ...item, isCurrent };
		});
	}, [list, params.fundID]);

	const currentCategoryList = useMemo(() => {
		const groupedByCategories = groupBy(listWithCurrentItemMarked, 'ec001');
		const result = groupedByCategories[dict[currentCategory]?.categoryId];
		return result || [];
	}, [listWithCurrentItemMarked, dict, currentCategory]);

	useEffect(() => {
		let current = '';

		currentCategoryList.forEach((item) => {
			const hasCurrentItem = item.result.some((result) => result.fundID === params.fundID);
			if (hasCurrentItem) current = item.id;
		});

		if (current) setCurrentFund(current);
		if (!current) setCurrentFund(currentCategoryList[0]?.id);
	}, [currentCategoryList, params.fundID]);

	const goToFund = useCallback(() => {
		const currentItem = find(listWithCurrentItemMarked, (item) => item.id === Number(currentFund));

		if (!currentItem || currentItem.isCurrent) return;

		const result = currentItem.result?.[0];

		if (!result) return;

		const cat = result.ec001;
		const fundID = result.fundID;

		if (cat && fundID) {
			let catpath = '';

			if (cat === 1) catpath = 'onshore';
			if (cat === 2) catpath = 'offshore';
			if (cat === 3) catpath = 'etf';

			history.push(`/fund/${catpath}/${fundID}`);
		}
	}, [listWithCurrentItemMarked, currentFund, history]);

	useEffect(() => {
		if (currentFund && window.innerWidth < 768) {
			goToFund();
		}
	}, [currentFund, goToFund]);

	useEffect(() => {
		setPathName(dict[category]?.pathName);
	}, [category, dict]);

	const route = useMemo(() => {
		if (params.fundID && location.pathname) return location.pathname.replace(`/${params.fundID}`, '');
		return '';
	}, [location.pathname, params.fundID]);

	const { stockInfo, isLoading, isError } = useStockItem(params.fundID, route);

	const parentStock = useMemo(() => {
		return stockInfo?.resultP?.[0] || {};
	}, [stockInfo]);

	const [type, setType] = useState(''); // todo

	useEffect(() => {
		// todo
		if (parentStock.ec001 === 1) setType('onshore');
		if (parentStock.ec001 === 2) setType('offshore');
	}, [parentStock.ec001]);

	const currentStock = useMemo(() => {
		if (parentStock?.result?.length) {
			return (
				find(parentStock.result, (item) => {
					return item.fundID === params.fundID;
				}) || {}
			);
		}
		return {};
	}, [parentStock.result, params.fundID]);

	const [form, setForm] = useState({
		durationType: 'year',
		year: '',
		startYear: '',
		startMonth: '',
		endYear: '',
		endMonth: '',
	});

	const years = useMemo(() => {
		if (currentStock.dividendYear) {
			return currentStock.dividendYear.sort().reverse();
		}
		return [];
	}, [currentStock]);

	useEffect(() => {
		if (years.length && !form.year) {
			setForm({ ...form, year: years[0] });
		}
	}, [form, years]);

	const [tab, setTab] = useState('data');

	useEffect(() => {
		if (!currentStock.ec312 && tab === 'dividend') {
			setTab('basic');
		}
	}, [currentStock.ec312, tab]);

	useEffect(() => {
		if (location?.state?.tab) {
			setTab(location.state.tab);
		}
	}, [location]);

	useEffect(() => {
		if (location?.state?.tab) {
			setTab(location.state.tab);
		}
	}, [location]);

	const [showError, setShowError] = useState(false);

	useEffect(() => {
		if (form.durationType === 'yearmonth') {
			if (form.startYear && form.endYear) {
				if (form.startYear > form.endYear) {
					return setShowError(true);
				}

				if (form.startYear === form.endYear) {
					if (form.startMonth > form.endMonth) {
						return setShowError(true);
					}
				}
			}

			setShowError(false);
		}
	}, [form]);

	const [stockDividend, setStockDividend] = useState({});

	const getStockDividend = useCallback(() => {
		if (showError) return false;

		const start = form.startYear + form.startMonth.toString().padStart(2, '0');
		const end = form.endYear + form.endMonth.toString().padStart(2, '0');

		fetcher('WebSiteStockDividendRequest', {
			ec001: '',
			mfundID: '',
			fundID: params.fundID,
			year: form.durationType === 'year' ? form.year : '',
			start: form.durationType === 'yearmonth' ? start : '',
			end: form.durationType === 'yearmonth' ? end : '',
		}).then((response) => {
			setStockDividend(response);
		});
	}, [params.fundID, form.durationType, form.year, form.startYear, form.startMonth, form.endYear, form.endMonth, showError]);

	const prevFormYear = usePrevious(form.year);

	useEffect(() => {
		if (!prevFormYear && form.year) {
			getStockDividend();
		}
	}, [prevFormYear, form.year, getStockDividend]);

	const dividendDownload = useMemo(() => {
		let pdf = {};
		if (type === 'onshore') pdf = stockDividend.pdf01;
		if (type === 'offshore') pdf = stockDividend.pdf02;
		return pdf;
	}, [type, stockDividend.pdf01, stockDividend.pdf02]);

	const { stockPrice, isError: stockPriceError } = useStockPrice(params.fundID);
	const { result, result30 } = stockPrice;

	useEffect(() => {
		if (stockPriceError || isError) {
			history.push('/not-found');
		}
	}, [history, isError, stockPriceError]);

	// const last30Days = useMemo(() => {
	// 	const list = sortBy(result30, 'navDate').reverse();
	// 	const finalList = [[], [], []];
	// 	each(list, (item, index) => {
	// 		const obj = { ...item };
	// 		const date = getTimestamp(item.navDate);
	// 		obj.date = moment(date).format('YYYY/MM/DD');
	// 		if (index < 10) return finalList[0].push(obj);
	// 		if (index < 20) return finalList[1].push(obj);
	// 		if (index < 30) finalList[2].push(obj);
	// 	});

	// 	return finalList;
	// }, [result30]);

	const last30DaysV2 = useMemo(() => {
		const list = sortBy(result30, 'navDate').reverse();
		const finalList = [[], []];
		each(list, (item, index) => {
			const obj = { ...item };
			const date = getTimestamp(item.navDate);
			obj.date = moment(date).format('YYYY/MM/DD');
			if (index < 15) return finalList[0].push(obj);
			if (index < 30) finalList[1].push(obj);
		});

		return finalList;
	}, [result30]);

	const last30DaysV3 = useMemo(() => {
		const list = sortBy(result30, 'navDate').reverse();
		return list.map((item) => {
			const obj = { ...item };
			const date = getTimestamp(item.navDate);
			obj.date = moment(date).format('YYYY/MM/DD');
			return obj;
		});
	}, [result30]);

	// const [currentPopup, setCurrentPopup] = useState({});
	// const [popupState, setPopupState] = useState(false);

	const graphData = useMemo(() => {
		const values =
			result?.map((data) => {
				let date = '';
				if (data.navDate) {
					date = data.navDate.split('(')[1].split('+')[0];
				}
				return [parseFloat(date), data.nav];
			}) || [];

		return values;
	}, [result]);

	const condition = useMemo(() => {
		if (currentStock.changeRange < 0) return 'bear';
		return 'bull';
	}, [currentStock.changeRange]);

	const [altForm, setAltForm] = useState({
		radio: 'last30', // last30, custom
		year: '',
		month: '',
	});

	// 新增自訂區間數據狀態
	const [customPeriodData, setCustomPeriodData] = useState([]);
	const [showCustomPeriod, setShowCustomPeriod] = useState(false);
	const [customPeriodDataV3, setCustomPeriodDataV3] = useState([]);

	// 初始化年月數據
	useEffect(() => {
		const currentDate = new Date();
		const currentYear = currentDate.getFullYear().toString();
		// 月份需要+1因為getMonth()是從0開始，並補0
		const currentMonth = (currentDate.getMonth() + 1).toString().padStart(2, '0');

		setAltForm(prev => ({
			...prev,
			year: currentYear,
			month: currentMonth,
		}));
	}, []);

	// 生成過去36個月的年月組合
	const monthsArray = useMemo(() => {
		const currentDate = new Date();
		const result = [];

		// 從當前月開始，往前推36個月
		for (let i = 0; i < 36; i++) {
			const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
			const year = date.getFullYear().toString();
			const month = (date.getMonth() + 1).toString().padStart(2, '0');

			result.push({
				year,
				month,
				label: `${year}年${month}月`
			});
		}

		return result;
	}, []);

	// 根據月份陣列生成年份選項（不重複）
	const yearOptions = useMemo(() => {
		const years = [...new Set(monthsArray.map(item => item.year))];
		return years.map(year => ({
			value: year,
			label: `${year}年`
		}));
	}, [monthsArray]);

	// 根據選擇的年份過濾可用的月份選項
	const monthOptions = useMemo(() => {
		const filteredMonths = monthsArray
			.filter(item => item.year === altForm.year)
			.map(item => ({
				value: item.month,
				label: `${item.month}月`
			}));

		return filteredMonths;
	}, [monthsArray, altForm.year]);

	// 處理前一個月按鈕點擊 - 修改為使用月份陣列
	const handlePrevMonth = useCallback(() => {
		const currentIndex = monthsArray.findIndex(
			item => item.year === altForm.year && item.month === altForm.month
		);

		if (currentIndex < monthsArray.length - 1) {
			const prevMonth = monthsArray[currentIndex + 1];
			setAltForm(prev => ({
				...prev,
				year: prevMonth.year,
				month: prevMonth.month,
				radio: 'custom'
			}));
		}
	}, [altForm.year, altForm.month, monthsArray]);

	// 處理下一個月按鈕點擊 - 修改為使用月份陣列
	const handleNextMonth = useCallback(() => {
		const currentIndex = monthsArray.findIndex(
			item => item.year === altForm.year && item.month === altForm.month
		);

		if (currentIndex > 0) {
			const nextMonth = monthsArray[currentIndex - 1];
			setAltForm(prev => ({
				...prev,
				year: nextMonth.year,
				month: nextMonth.month,
				radio: 'custom'
			}));
		}
	}, [altForm.year, altForm.month, monthsArray]);

	const url = useMemo(() => {
		let url = '';
		if (BaseUrl === '/') {
			url = window.location.origin;
		}
		return url;
	}, []);

	// 處理查詢按鈕點擊
	const handleQuery = useCallback(() => {
		if (altForm.radio === 'custom') {
			axios.get(`${url || BaseUrl}/api/WebSite/stockPrice?fundID=${params.fundID}&y=${altForm.year}&m=${altForm.month}`)
				.then((response) => {
					if (response && response.data && response.data.result30) {
						const list = sortBy(response.data.result30, 'navDate').reverse();
						const finalList = [[], []];

						each(list, (item, index) => {
							const obj = { ...item };
							const date = getTimestamp(item.navDate);
							obj.date = moment(date).format('YYYY/MM/DD');
							if (index < 15) return finalList[0].push(obj);
							if (index < 30) finalList[1].push(obj);
						});

						const finalListV3 = list.map((item) => {
							const obj = { ...item };
							const date = getTimestamp(item.navDate);
							obj.date = moment(date).format('YYYY/MM/DD');
							return obj;
						});

						setCustomPeriodData(finalList);
						setShowCustomPeriod(true);
						setCustomPeriodDataV3(finalListV3);
					}
				});
		} else {
			// 顯示近30筆淨值
			setShowCustomPeriod(false);
		}
	}, [altForm.month, altForm.radio, altForm.year, params.fundID, url]);

	if (isLoading || isError) {
		return <Loading />;
	}

	const { ec314,
		// ec315,
		twName, nav, navDate, changeRange, ec312, contents, ec006, etf003, ec005, etf004, etf001, ec009, ec010, ec012, ec013, ec021, ec024, etf005, etf006, etf007, etf008, etf002, etf009, fundID } = currentStock;

	const change = (changeRange * 100).toFixed(2).replace('-', '');

	return (
		<div className='FundItem' id='ETFItem'>
			<Meta data={currentStock} />

			<Breadcrumbs
				data={[
					{ url: '', name: '基金總覽', guid: 'a' },
					{ url: '', name: pathName, guid: 'b' },
				]}
			/>

			<div className='common-pad-wrap'>
				<div className='extra-pad'>
					<div className='select-box'>
						<div className='group'>
							<div className='group-title'>選擇基金</div>

							<div className='select-flex'>
								<select
									className='category'
									value={currentCategory}
									onChange={(event) => {
										setCurrentCategory(event.target.value);
									}}>

									<option value='onshore'>境內基金</option>
									<option value='offshore'>境外基金</option>
									<option value='etf'>ETF</option>
								</select>

								<select
									className='category grow'
									value={currentFund}
									onChange={(event) => {
										setCurrentFund(event.target.value);
									}}>
									<option value=''></option>
									{currentCategoryList.map((item) => {
										return (
											<option value={item.id} key={item.id}>
												{item.twName}
											</option>
										);
									})}
								</select>
							</div>
						</div>

						<div className='button-wrap'>
							<div className='button' onClick={goToFund}>查詢</div>
						</div>
					</div>

					<div className='flex-top'>
						<div className='left'>
							<h1>{twName}</h1>
							<div className='sub'>{etf002}</div>
						</div>

						<div className='right'>{ec314 === 1 ? <div className='hot'>hot</div> : null}</div>
					</div>

					<div className='flex-section'>
						<div className='left'>
							<div className='info-board'>
								<div className='group'>
									<div className='head'>最新淨值</div>
									<div className='info'>{nav}</div>
									<div className='line'></div>
								</div>

								<div className='group'>
									<div className='head'>淨值日期</div>
									<div className='info'>{navDate}</div>
									<div className='line'></div>
								</div>

								<div className='group'>
									<div className='head'>
										與前日
										<span className='br'>
											<br />
										</span>
										漲跌幅(%)
									</div>
									<div className='info'>
										<div className={`range ${condition}`}>
											<div className='text'>{change}%</div>
											<div className='icon'></div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className='downloads'>
							{parentStock.pdf?.map((pdf) => {
								const { url, urlType, name } = pdf;
								return (
									<div className='link-wrap' key={`u${url}`}>
										<a href={url} target={urlType} rel='noreferrer noopener'>
											{name}
										</a>
									</div>
								);
							})}
						</div>
					</div>

					<div className='tabs'>
						<div
							className={`tab ${tab === 'basic' && 'active'}`}
							onClick={() => {
								setTab('basic');
							}}>
							基本
							<span className='br'>
								<br />
							</span>
							資料
						</div>

						<div
							className={`tab ${tab === 'data' && 'active'}`}
							onClick={() => {
								setTab('data');
							}}>
							淨值
							<span className='br'>
								<br />
							</span>
							走勢
						</div>

						{ec312 ? (
							<div
								className={`tab ${tab === 'dividend' && 'active'}`}
								onClick={() => {
									setTab('dividend');
								}}>
								配息
								<span className='br'>
									<br />
								</span>
								資訊
							</div>
						) : null}

						{/* <Link to={`/fund/etf/pcf?fundID=${currentStock.fundID}`}
							className={`tab ${tab === 'list' && 'active'}`}>
							申購買回
							<span className='br'>
								<br />
							</span>
							清單
						</Link> */}

						<div
							className='tab'>
							<Link to='/fund/etf/estimate'>
								即時預
								<span className='br'>
									<br />
								</span>
								估淨值
							</Link>
						</div>

						<div
							className='tab oneline'>
							<Link to={`/fund/etf/pcf?fundID=${fundID}`}>
								成分股
							</Link>
						</div>

						{/* <div
							className='tab'>
							<Link to='/etfdaily'>
								ETF
								<span className='br'>
									<br />
								</span>
								日訊
							</Link>
						</div> */}
					</div>

					<div className='tab-contents'>
						<div className={`tab-content ${tab === 'basic' && 'active'}`}>
							<div className='section-title'>
								<div className='text'>基金特色與指數介紹</div>
								<div className='line'></div>
							</div>

							<div className='contents' dangerouslySetInnerHTML={{ __html: contents }}></div>

							<div className='section-title'>
								<div className='text'>基金基本資料</div>
								<div className='line'></div>
							</div>

							<div className='flex-table'>
								<div className='item-group'>
									<div className='head'>股票代號</div>
									<div className='info'>
										{etf002}
									</div>
								</div>

								<div className='item-group'>
									<div className='head'>股票簡稱</div>
									<div className='info'>
										{etf003}
									</div>
								</div>

								<div className='item-group'>
									<div className='head'>成立日</div>
									<div className='info'>{ec005}</div>
								</div>

								<div className='item-group'>
									<div className='head'>風險等級</div>
									<div className='info'>{ec009}</div>
								</div>

								<div className='item-group'>
									<div className='head'>掛牌日期</div>
									<div className='info'>{etf004}</div>
								</div>

								<div className='item-group'>
									<div className='head'>收益分配</div>
									<div className='info'>{etf001}</div>
								</div>

								<div className='item-group'>
									<div className='head'>基金類型</div>
									<div className='info'>{etf009}</div>
								</div>

								<div className='item-group'>
									<div className='head'>基金型態</div>
									<div className='info'>{ec021}</div>
								</div>

								<div className='item-group'>
									<div className='head'>保管銀行</div>
									<div className='info'>{ec010}</div>
								</div>

								<div className='item-group'>
									<div className='head'>經理年費</div>
									<div className='info'>{ec012}</div>
								</div>

								<div className='item-group'>
									<div className='head'>保管年費</div>
									<div className='info'>{ec013}</div>
								</div>

								<div className='item-group'>
									<div className='head'>投資區域</div>
									<div className='info'>{ec024}</div>
								</div>

								<div className='item-group'>
									<div className='head'>計價幣別</div>
									<div className='info'>{ec006}</div>
								</div>

								<div className='item-group'>
									<div className='head'>追蹤指數</div>
									<div className='info'>{etf005}</div>
								</div>

								<div className='item-group'>
									<div className='head'>申購／買回基本單位</div>
									<div className='info'>{etf006}</div>
								</div>
							</div>

							<div className='section-title'>
								<div className='text'>參與券商</div>
								<div className='line'></div>
							</div>

							<div className='backend-table' dangerouslySetInnerHTML={{ __html: etf007 }}></div>

							<div className='section-title'>
								<div className='text'>流動量提供券商</div>
								<div className='line'></div>
							</div>

							<div className='backend-table' dangerouslySetInnerHTML={{ __html: etf008 }}></div>
						</div>

						<div className={`tab-content ${tab === 'data' && 'active'}`}>
							<div className='section-title'>
								<div className='text'>淨值走勢</div>
								<div className='line'></div>
							</div>

							<ChartComponent graphData={graphData} />

							{/* <div className='stock-name'>
								{currentStock.twName}-{currentStock.ec018}
								{currentStock.ec006}
							</div> */}

							<div className='special-wrap'>
								<div className='section-title'>
									<div className='text'>歷史淨值</div>
									<div className='line'></div>
								</div>

								<div className='download-wrap'>
									<a href={`${url || BaseUrl}/api/WebSite/priceexcel/${params.fundID}`} target='_blank' rel='noreferrer noopener' className='download-excel'>
										<div className='icon'>
											<img src='/assets/download.svg' alt='' />
										</div>
										<div className='text'>下載近三年資料</div>
									</a>
								</div>
							</div>

							<div className='search-box'>
								<div className='flex-between'>
									<div className='group'>
										<div
											className='radio-wrap'
											onClick={() => {
												setAltForm({ ...altForm, radio: 'last30' });
											}}>
											<input
												type='radio'
												name='netRadio'
												value='last30'
												checked={altForm.radio === 'last30'}
											/>

											<div className='outer'>
												<div className='inner'></div>
											</div>

											<div className='text'>
												<div className='label'>近30筆淨值</div>
											</div>
										</div>

										<div
											className='radio-wrap'
											htmlFor='radioYearMonth'
											onClick={() => {
												setAltForm({ ...altForm, radio: 'custom' });
											}}>
											<input
												type='radio'
												name='netRadio'
												value='yearmonth'
												checked={altForm.radio === 'custom'}
												onChange={() => {
													setAltForm({ ...altForm, radio: 'custom' });
												}}
											/>

											<div className='outer'>
												<div className='inner'></div>
											</div>

											<div className='text'>
												<div className='label'>自訂期間</div>

												{/* 修改按鈕以增強點擊區域 */}
												<div
													className='icon-wrap prev'
													onClick={(e) => {
														e.stopPropagation();
														handlePrevMonth();
													}}
												>
													<img src='/assets/prev.svg' alt='上一個月' />
												</div>

												<div className='flex-mob'>
													<select
														className='alt'
														value={altForm.year}
														onChange={(e) => {
															const newYear = e.target.value;
															// 當年份改變時，選擇該年份的第一個可用月份
															const availableMonths = monthsArray
																.filter(item => item.year === newYear)
																.map(item => item.month);

															const newMonth = availableMonths.length ? availableMonths[0] : altForm.month;

															setAltForm({
																...altForm,
																year: newYear,
																month: newMonth
															});
														}}>
														{yearOptions.map(year => (
															<option key={year.value} value={year.value}>{year.label}</option>
														))}
													</select>

													<div className='to'></div>

													<select
														className='alt'
														value={altForm.month}
														onChange={(e) => {
															setAltForm({ ...altForm, month: e.target.value });
														}}>
														{monthOptions.map(month => (
															<option key={month.value} value={month.value}>{month.label}</option>
														))}
													</select>
												</div>

												{/* 修改按鈕以增強點擊區域 */}
												<div
													className='icon-wrap next'
													onClick={(e) => {
														e.stopPropagation();
														handleNextMonth();
													}}
												>
													<img src='/assets/next.svg' alt='下一個月' />
												</div>
											</div>
										</div>
									</div>

									<div className='button-wrap'>
										<div className='common-button query' onClick={handleQuery}>
											查詢
										</div>
									</div>
								</div>
							</div>

							{/* {ec315 === 0 ? (
								<div className='net-flex'>
									{last30Days.map((group, index) => {
										return (
											<div className={`item-wrap ${!index && 'first'}`} key={`neee${index}`}>
												<div className='item'>
													<div className='row th'>
														<div className='cell c1'>日期</div>
														<div className='cell'>淨值</div>
														<div className='cell c3'>與前日漲跌幅(%)</div>
													</div>

													{group.map((day) => {
														const { guid, nav, date, changeRange } = day;

														let condition = '';

														if (changeRange > 0) condition = 'bull';
														if (changeRange < 0) condition = 'bear';

														const change = (changeRange * 100).toFixed(2).replace('-', '');

														return (
															<div className='row' key={`net${guid}`}>
																<div className='cell c1'>{date}</div>
																<div className='cell'>{nav}</div>
																<div className='cell c3'>
																	<div className={`range tall ${condition}`}>
																		<div className='text'>{change}%</div>
																		<div className='icon'></div>
																	</div>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>
							) : null}

							{ec315 === 1 ? (
								<div className='net-flex'>
									{last30DaysV2.map((group, index) => {
										return (
											<div className={`item-wrap-half ${!index && 'first'}`} key={`nee${index}`}>
												<div className='item'>
													<div className='row th'>
														<div className='cell c1'>
															<div className='flex-c'>日期</div>
														</div>
														<div className='cell'>
															<div className='flex-c'>淨值</div>
														</div>
														<div className='cell c3'>
															<div className='flex-c'>與前日漲
																<span className='br'>
																	<br />
																</span>
																跌幅(%)
															</div>
														</div>
														<div className='cell c4'>
															<div className='flex-c'>淨值組成
																<span className='br'>
																	<br />
																</span>
																項目
															</div>
														</div>
													</div>

													{group.map((day) => {
														const { guid, nav, date, changeRange } = day;

														let condition = '';

														if (changeRange > 0) condition = 'bull';
														if (changeRange < 0) condition = 'bear';

														const change = (changeRange * 100).toFixed(2).replace('-', '');

														return (
															<div className='row' key={`net${guid}`}>
																<div className='cell c1'>{date}</div>
																<div className='cell'>{nav}</div>
																<div className='cell c3'>
																	<div className={`range tall ${condition}`}>
																		<div className='text'>{change}%</div>
																		<div className='icon'></div>
																	</div>
																</div>
																<div className='cell c4'>
																	<div className='details-icon' onClick={() => {
																		setCurrentPopup(day);
																		setPopupState(true);
																	}}></div>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										);
									})}
								</div>
							) : null} */}

							<div className='warning-wrap'>
								<div className='text'>*ETF僅提供自平準金實施後淨值組成資料</div>
							</div>

							{!showCustomPeriod && (
								<div className='flex-tables d'>
									{last30DaysV2.map((group, index) => {
										return (
											<div className='table-wrap' key={`ne${index}`}>
												<table>
													<thead>
														<tr>
															<th>日期</th>
															<th>基本面額<br />(A)</th>
															<th>收益平準<br />金(B)*</th>
															<th>資本損益<br />平準金<br />(C)**</th>
															<th>基金淨值<br />(A+B+C)</th>
															<th>與前日漲<br />跌幅(%)</th>
														</tr>
													</thead>

													<tbody>
														{group.map((item, index) => {
															const { date, faceValue, canDis, capitalGain, nav, changeRange } = item;

															const range = (changeRange * 100).toFixed(2).replace('-', '');

															const getBullBear = (string) => {
																const trimmed = string.replace('%', '');
																const num = Number(trimmed);
																const isNumber = !Number.isNaN(num);

																if (isNumber && num > 0) return 'bull';
																if (isNumber && num < 0) return 'bear';

																return '';
															}

															return (
																<tr key={`row${index}`}>
																	<td>{date}</td>

																	<td>{faceValue}</td>

																	<td>{canDis}</td>

																	<td>{capitalGain}</td>

																	<td>{nav}</td>

																	<td>
																		<div className={`range ${getBullBear(changeRange.toString())}`}>
																			<div className='text'>{range}%</div>
																			<div className='icon'></div>
																		</div>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>
										);
									})}
								</div>
							)}

							{!showCustomPeriod && (
								<div className='table-wrap m'>
									<table>
										<thead>
											<tr>
												<th>日期</th>
												<th>基本面額<br />(A)</th>
												<th>收益平準<br />金(B)*</th>
												<th>資本損益<br />平準金<br />(C)**</th>
												<th>基金淨值<br />(A+B+C)</th>
												<th>與前日漲<br />跌幅(%)</th>
											</tr>
										</thead>

										<tbody>
											{last30DaysV3.map((item, index) => {
												const { date, faceValue, canDis, capitalGain, nav, changeRange } = item;

												const range = (changeRange * 100).toFixed(2).replace('-', '');

												const getBullBear = (string) => {
													const trimmed = string.replace('%', '');
													const num = Number(trimmed);
													const isNumber = !Number.isNaN(num);

													if (isNumber && num > 0) return 'bull';
													if (isNumber && num < 0) return 'bear';

													return '';
												}

												return (
													<tr key={`row${index}`}>
														<td>{date}</td>

														<td>{faceValue}</td>

														<td>{canDis}</td>

														<td>{capitalGain}</td>

														<td>{nav}</td>

														<td>
															<div className={`range ${getBullBear(changeRange.toString())}`}>
																<div className='text'>{range}%</div>
																<div className='icon'></div>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}

							{showCustomPeriod && (
								<div className='flex-tables d'>
									{customPeriodData.map((group, index) => {
										return (
											<div className='table-wrap' key={`net${index}`}>
												<table>
													<thead>
														<tr>
															<th>日期</th>
															<th>基本面額<br />(A)</th>
															<th>收益平準<br />金(B)*</th>
															<th>資本損益<br />平準金<br />(C)**</th>
															<th>基金淨值<br />(A+B+C)</th>
															<th>與前日漲<br />跌幅(%)</th>
														</tr>
													</thead>

													<tbody>
														{group.map((item, index) => {
															const { date, faceValue, canDis, capitalGain, nav, changeRange } = item;

															const range = (changeRange * 100).toFixed(2).replace('-', '');

															const getBullBear = (string) => {
																const trimmed = string.replace('%', '');
																const num = Number(trimmed);
																const isNumber = !Number.isNaN(num);

																if (isNumber && num > 0) return 'bull';
																if (isNumber && num < 0) return 'bear';

																return '';
															}

															return (
																<tr key={`row${index}`}>
																	<td>{date}</td>

																	<td>{faceValue}</td>

																	<td>{canDis}</td>

																	<td>{capitalGain}</td>

																	<td>{nav}</td>

																	<td>
																		<div className={`range ${getBullBear(changeRange.toString())}`}>
																			<div className='text'>{range}%</div>
																			<div className='icon'></div>
																		</div>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>
										);
									})}
								</div>
							)}

							{showCustomPeriod && (
								<div className='table-wrap m'>
									<table>
										<thead>
											<tr>
												<th>日期</th>
												<th>基本面額<br />(A)</th>
												<th>收益平準<br />金(B)*</th>
												<th>資本損益<br />平準金<br />(C)**</th>
												<th>基金淨值<br />(A+B+C)</th>
												<th>與前日漲<br />跌幅(%)</th>
											</tr>
										</thead>

										<tbody>
											{customPeriodDataV3.map((item, index) => {
												const { date, faceValue, canDis, capitalGain, nav, changeRange } = item;

												const range = (changeRange * 100).toFixed(2).replace('-', '');

												const getBullBear = (string) => {
													const trimmed = string.replace('%', '');
													const num = Number(trimmed);
													const isNumber = !Number.isNaN(num);

													if (isNumber && num > 0) return 'bull';
													if (isNumber && num < 0) return 'bear';

													return '';
												}

												return (
													<tr key={`row${index}`}>
														<td>{date}</td>

														<td>{faceValue}</td>

														<td>{canDis}</td>

														<td>{capitalGain}</td>

														<td>{nav}</td>

														<td>
															<div className={`range ${getBullBear(changeRange.toString())}`}>
																<div className='text'>{range}%</div>
																<div className='icon'></div>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</div>

						<div className='notes-wrap'>
							<div className='note'>* &nbsp;係指本基金現金股利、利息收入及收益平準金扣除本基金應負擔之費用，有關收益平準之說明請詳閱本基金信託契約或公開說明書。</div>

							<div className='note'>**含已實現與未實現資本損益，以及已實現與未實現資本損益平準金，有關收益平準之說明請詳閱本基金信託契約或公開說明書。</div>
						</div>

						<div className={`tab-content ${tab === 'dividend' && 'active'}`}>
							<div className='section-title'>
								<div className='text'>最近一次配息</div>
								<div className='line'></div>
							</div>

							<div className='last-dividend'>
								<div className='wrap'>
									<div className='item'>
										<div className='head'>每單位配息金額</div>
										<div className='content'>{currentStock.ec01022}</div>
										<div className='triangle'></div>
									</div>
								</div>

								<div className='wrap'>
									<div className='item'>
										<div className='head'>除息日</div>
										<div className='content'>{currentStock.ec01034}</div>
										<div className='triangle'></div>
									</div>
								</div>

								{/* <div className='wrap'>
									<div className='item'>
										<div className='head'>配息基準日</div>
										<div className='content'>{currentStock.ec01013}</div>
										<div className='triangle'></div>
									</div>
								</div> */}

								<div className='wrap'>
									<div className='item'>
										<div className='head'>評價日</div>
										<div className='content'>{stockDividend?.fundresult?.[0]?.ec01036}</div>
										<div className='triangle'></div>
									</div>
								</div>

								<div className='wrap'>
									<div className='item'>
										<div className='head'>配息發放日</div>
										<div className='content'>{stockDividend?.fundresult?.[0]?.ec01031}</div>
										<div className='triangle'></div>
									</div>
								</div>
							</div>

							<div className='section-title'>
								<div className='text'>歷史配息紀錄</div>
								<div className='line'></div>
							</div>

							<div className='dividend-download-wrap'>
								<a href={dividendDownload?.url} target='_blank' rel='noreferrer noopener'>
									<div className='icon'></div>
									<div className='text'>{dividendDownload?.name}</div>
								</a>
							</div>

							<div className='search-box'>
								<div className='flex-between'>
									<div className='group'>
										<div
											className='radio-wrap'
											onClick={() => {
												setForm({ ...form, durationType: 'year' });
											}}>
											<input
												type='radio'
												name='durationType'
												value='year'
												checked={form.durationType === 'year'}
												onChange={() => {
													setForm({ ...form, durationType: 'year' });
												}}
											/>

											<div className='outer'>
												<div className='inner'></div>
											</div>

											<div className='text'>
												<div className='label'>年度配息</div>

												<select
													className='year'
													value={form.year}
													onChange={(event) => {
														setForm({ ...form, year: event.target.value });
													}}>
													{years.map((year) => {
														return (
															<option value={year} key={`y${year}`}>
																{year}年
															</option>
														);
													})}
												</select>
											</div>
										</div>

										<div
											className='radio-wrap'
											htmlFor='radioYearMonth'
											onClick={() => {
												setForm({ ...form, durationType: 'yearmonth' });
											}}>
											<input
												type='radio'
												name='durationType'
												value='yearmonth'
												checked={form.durationType === 'yearmonth'}
												onChange={() => {
													setForm({ ...form, durationType: 'yearmonth' });
												}}
											/>

											<div className='outer'>
												<div className='inner'></div>
											</div>

											<div className='text'>
												<div className='label'>自訂期間</div>

												<div className='flex-mob'>
													<YearMonthSelector
														selectedYear={form.startYear}
														selectedMonth={form.startMonth}
														updateValues={(year, month) => {
															setForm({ ...form, startYear: year, startMonth: month });
														}}
													/>

													<div className='to'></div>

													<YearMonthSelector
														selectedYear={form.endYear}
														selectedMonth={form.endMonth}
														updateValues={(year, month) => {
															setForm({ ...form, endYear: year, endMonth: month });
														}}
													/>

													{showError ? <div className='error-message'>查詢結束日需大於起始日</div> : null}
												</div>
											</div>
										</div>
									</div>

									<div className='button-wrap'>
										<div className='common-button query' onClick={getStockDividend}>
											查詢
										</div>
									</div>
								</div>
							</div>

							{stockDividend && stockDividend.fundresult && stockDividend.fundresult.length ? (
								<div className='dividend-table-wrap fundItem'>
									<table>
										<thead>
											<tr>
												<th>每單位配息金額</th>
												<th>評價日</th>
												<th>除息日</th>
												{/* <th>配息基準日</th> */}
												<th>配息發放日</th>
												{/* <th>年化配息率</th> */}
												<th>配息頻率</th>
											</tr>
										</thead>

										<tbody>
											{stockDividend.fundresult?.map((row, i) => {
												const { guid, ec01022,
													// ec01013,
													ec01031, ec01034, ec01012, ec01036 } = row;

												return (
													<tr key={`g${guid}${i}`}>
														<td>{ec01022}</td>
														<td>{ec01036}</td>
														<td>{ec01034}</td>
														{/* <td>{ec01013}</td> */}
														<td>{ec01031}</td>
														{/* <td>{ec01033}</td> */}
														<td>{ec01012}</td>
													</tr>
												);
											})}
										</tbody>
									</table>

									{stockInfo.resultP?.[0].ec003 ? <div className='warning'>*{stockInfo.resultP[0].ec003}</div> : null}
								</div>
							) : (
								<div className='nothing'>
									<div className='img'>
										<img src={none} alt='' />
									</div>
									<div className='text'>無符合搜尋條件的配息資料</div>
								</div>
							)}
						</div>

						<div className='common-button-wrap'>
							<Link to={stockInfo.breadcrumb?.[1]?.route || ''} className='common-button arrow'>
								<div className='text'>看其他基金</div>
								<div className='arrow'></div>
							</Link>
						</div>
					</div>
				</div>
			</div>

			{/* <div className={`popup-wrap ${popupState ? 'active' : ''}`}>
				<div className='popup-content'>
					<div className='close' onClick={() => { setPopupState(false) }}></div>

					<div className='title'>{twName}-{ec006}</div>
					<div className='date'>{currentPopup.date}</div>

					<div className='description'>本基金淨值主要由｢面額｣、｢可分配投資收益｣及｢資本利得｣所組成。｢可分配投資收益｣所包含的｢收益平準金｣係指自本基金成立日起，凡本基金申購或買回價金中，計算日時屬於原受益人每受益權單位淨資產價值中所累積之收入扣除費用部分屬之。為使本基金配息政策穩定，本基金之配息來源可能包含前述｢收益平準金｣，相關資訊如下：</div>

					<div className='table-title'>基金淨值組成項目</div>
					<div className='table'>
						<div className='row odd'>
							<div className='heading'>(A)面額</div>
							<div className='value'>{currentPopup.faceValue}</div>
						</div>

						<div className='row'>
							<div className='heading'>(B)可分配投資收益*</div>
							<div className='value'>{currentPopup.canDis}</div>
						</div>

						<div className='row odd'>
							<div className='heading'>(C)資本利得**</div>
							<div className='value'>{currentPopup.capitalGain}</div>
						</div>

						<div className='row'>
							<div className='heading'>(D)基金淨值(元)【A+B+C】</div>
							<div className='value'>{currentPopup.nav}</div>
						</div>
					</div>

					<div className='details'>
						*可分配投資收益係指:現金股利、利息收入等收入及收益平準金扣除本基金應負擔之費用。
						<br />
						**資本利得係指：已實現與未實現資本損益，以及已實現與未實現資本損益平準金。
					</div>
				</div>
			</div> */}
		</div>
	);
};

export default ETFItem;
